import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card } from './ui/card';
import { Shield, RefreshCw } from 'lucide-react';
import { authAPI } from '../utils/api';
import { toast } from 'sonner';

const OTPVerification = ({ phoneNumber, onVerified, onBack }) => {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const handleVerify = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    try {
      // Pass the code up; the parent completes registration / pin-set which verifies it server-side
      await onVerified(otp);
    } catch (error) {
      console.error('Verification error:', error);
      toast.error(error.response?.data?.detail || 'Invalid code. Please try again.');
      setOtp('');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await authAPI.resendOTP(phoneNumber);
      toast.success('OTP resent successfully!');
      setCountdown(60);
      setCanResend(false);
      setOtp('');
    } catch (error) {
      console.error('Resend error:', error);
      toast.error('Failed to resend OTP. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleOTPChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setOtp(value);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && otp.length === 6) {
      handleVerify();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#064E3B] via-[#065F46] to-[#047857] flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 bg-white dark:bg-gray-800 shadow-2xl">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="h-20 w-20 rounded-full bg-[#84CC16] flex items-center justify-center">
            <Shield className="h-10 w-10 text-white" />
          </div>
        </div>

        {/* Title */}
        <h2 className="text-3xl font-bold text-center text-[#064E3B] dark:text-white mb-2">
          Verify Your Number
        </h2>
        <p className="text-center text-gray-600 dark:text-gray-400 mb-6">
          We sent a verification code to
          <br />
          <span className="font-bold text-[#064E3B] dark:text-white">{phoneNumber}</span>
        </p>

        {/* OTP Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Enter 6-digit code
          </label>
          <Input
            type="text"
            inputMode="numeric"
            value={otp}
            onChange={handleOTPChange}
            onKeyPress={handleKeyPress}
            placeholder="000000"
            className="text-center text-2xl font-bold tracking-widest"
            maxLength={6}
            autoFocus
            data-testid="otp-input"
          />
        </div>

        {/* Verify Button */}
        <Button
          onClick={handleVerify}
          disabled={otp.length !== 6 || loading}
          className="w-full bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold py-3 mb-4"
          data-testid="otp-verify-btn"
        >
          {loading ? 'Verifying...' : 'Verify & Continue'}
        </Button>

        {/* Resend OTP */}
        <div className="text-center mb-4">
          {canResend ? (
            <Button
              onClick={handleResend}
              disabled={resending}
              variant="ghost"
              className="text-[#064E3B] dark:text-[#84CC16] hover:underline"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${resending ? 'animate-spin' : ''}`} />
              {resending ? 'Resending...' : 'Resend OTP'}
            </Button>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Resend code in {countdown}s
            </p>
          )}
        </div>

        {/* Back Button */}
        <Button
          onClick={onBack}
          variant="outline"
          className="w-full"
        >
          Change Phone Number
        </Button>

        {/* Help Text */}
        <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-6">
          Code expires in 10 minutes. Check your WhatsApp messages.
        </p>
      </Card>
    </div>
  );
};

export default OTPVerification;
